import {
	createContext,
	useContext,
	useMemo,
	useState,
	useRef,
	useEffect,
} from "react"
import { useAuth } from "./AuthContext"
import { useCombinedPayments } from "../hooks/usePayments"
import { useSearchParams } from "react-router-dom"
import {
	ColumnFiltersState,
	Table,
	VisibilityState,
	OnChangeFn,
} from "@tanstack/react-table"
import { mapMovementsDataToTableMovements } from "../components/tables/transactions-table/data/movements/movementsData"
import {
	TableOrder,
	TableMovement,
	PaymentHashTable,
	TableData,
	TableView,
} from "../components/tables/transactions-table/data/types"
import { mapCombinedPaymentsToTableOrder } from "../components/tables/transactions-table/data/orders/ordersData"
import { useCustomers } from "../hooks/useCustomers"
import { mapCustomersDataToTableCustomers } from "../components/tables/transactions-table/data/customers/customersData"
import { set } from "cypress/types/lodash"

interface TableDataContextType {
	payments: TableOrder[]
	movements: TableMovement[]
	filteredPayments: TableOrder[]
	combinedPaymentsLoading: boolean
	updatePayment: (updatedPayment: TableOrder) => void
	currentView: TableView
	setView: (view: TableView) => void
	storeFilter: string[]
	setStoreFilter: (filter: string[]) => void
	columnFilters: ColumnFiltersState
	setColumnFilters: OnChangeFn<ColumnFiltersState>
	columnVisibility: VisibilityState
	setColumnVisibility: OnChangeFn<VisibilityState>
	tableRef: React.MutableRefObject<Table<TableData> | undefined>
	paymentHashtable: PaymentHashTable
	processingProgress: number
	customersData: any
	customersLoading: boolean
	movementsProcessingProgress: boolean
}

const TableDataContext = createContext<TableDataContextType | undefined>(
	undefined
)

export function TableDataProvider({
	children,
	defaultView = "orders",
}: {
	children: React.ReactNode
	defaultView?: TableView
}) {
	const { user, stores } = useAuth()
	const { data: combinedPayments, isLoading: combinedPaymentsLoading } =
		useCombinedPayments(
			user.id,
			user.roles.includes("pos") ? user.team_id : undefined
		)
	const { data: customersData, isLoading: customersLoading } = useCustomers()

	const [searchParams, setSearchParams] = useSearchParams()
	const currentView =
		(searchParams.get("tableView") as TableView) || defaultView

	const setView = (view: TableView) => {
		setSearchParams((params) => {
			params.set("tableView", view)
			return params
		})
	}

	const [updatedPayments, setUpdatedPayments] = useState<
		Map<string, TableOrder>
	>(new Map())

	const [processingProgress, setProcessingProgress] = useState(0)
	const totalPayments = combinedPayments?.payments?.length || 0

	const [partialPayments, setPartialPayments] = useState<TableOrder[]>([])
	const [partialHashtable, setPartialHashtable] = useState<PaymentHashTable>({})
	const [finalResults, setFinalResults] = useState<{
		order: TableOrder[]
		hashtable: PaymentHashTable
	}>({
		order: [],
		hashtable: {},
	})

	const [movementsProcessingProgress, setMovementsProcessingProgress] =
		useState(true)

	useEffect(() => {
		if (combinedPayments?.payments) {
			setProcessingProgress(0) // Reset progress at start
			mapCombinedPaymentsToTableOrder({
				payments: combinedPayments?.payments as any[], //TODO type this
				transactions: combinedPayments?.transactions,
				stores,
				onProcessed: (result) => {
					setPartialPayments((prev) => [...prev, result.order])
					setPartialHashtable((prev) => ({
						...prev,
						[result.hashtableEntry[0]]: result.hashtableEntry[1],
					}))
					setProcessingProgress((prev) => prev + 1) // Increment progress counter
				},
			}).then((result) => {
				setFinalResults(result)
				// Add null check before accessing length
				const totalCount = combinedPayments?.payments?.length ?? 0
				setProcessingProgress(totalCount) // Ensure we reach 100%
			})
		}
	}, [combinedPayments, stores])

	// Use partial results while full processing completes
	const displayPayments =
		finalResults.order.length > 0 ? finalResults.order : partialPayments
	const displayHashtable =
		Object.keys(finalResults.hashtable).length > 0
			? finalResults.hashtable
			: partialHashtable

	const payments = useMemo(() => {
		return displayPayments.map(
			(payment) => updatedPayments.get(payment.id) || payment
		)
	}, [displayPayments, updatedPayments])

	const filteredPayments = useMemo(
		() =>
			payments.filter(
				(payment) =>
					!["SENT", "OUTBOUND", "processed", "pending", "rejected"].includes(
						payment.status
					)
			),
		[payments]
	)

	const movements = useMemo(() => {
		var result
		if (!combinedPaymentsLoading) {
			result = mapMovementsDataToTableMovements({
				payments: combinedPayments?.payments as any[], // TODO type this
				transactions: combinedPayments?.transactions,
				paymentHashtable: displayHashtable,
			})
			setMovementsProcessingProgress(false)
		}
		return result ?? []
	}, [combinedPayments, displayHashtable])

	const updatePayment = (updatedPayment: TableOrder) => {
		setUpdatedPayments((prev) => {
			const next = new Map(prev)
			next.set(updatedPayment.id, updatedPayment)
			return next
		})
	}

	const mappedCustomers = useMemo(() => {
		return mapCustomersDataToTableCustomers(customersData)
	}, [customersData])

	const [storeFilter, setStoreFilter] = useState<string[]>([])

	const [ordersColumnFiltersState, setOrdersColumnFiltersState] =
		useState<ColumnFiltersState>([])
	const [movementsColumnFiltersState, setMovementsColumnFiltersState] =
		useState<ColumnFiltersState>([])
	const [customersColumnFiltersState, setCustomersColumnFiltersState] =
		useState<ColumnFiltersState>([])

	const setColumnFilters: OnChangeFn<ColumnFiltersState> = (updater) => {
		if (currentView === "orders") {
			setOrdersColumnFiltersState((old) => {
				return typeof updater === "function" ? updater(old) : updater
			})
		} else if (currentView === "movements") {
			setMovementsColumnFiltersState((old) => {
				return typeof updater === "function" ? updater(old) : updater
			})
		} else if (currentView === "customers") {
			setCustomersColumnFiltersState((old) => {
				return typeof updater === "function" ? updater(old) : updater
			})
		}
	}

	const setColumnVisibility: OnChangeFn<VisibilityState> = (updater) => {
		const newState =
			typeof updater === "function" ? updater(columnVisibilityState) : updater
		setColumnVisibilityState(newState)
	}

	const [columnVisibilityState, setColumnVisibilityState] =
		useState<VisibilityState>({
			store_name: false,
		})

	const tableRef = useRef<Table<TableData>>()

	const value = useMemo(
		() => ({
			payments,
			movements,
			filteredPayments,
			combinedPaymentsLoading,
			updatePayment,
			currentView,
			setView,
			storeFilter,
			setStoreFilter,
			columnFilters: {
				orders: ordersColumnFiltersState,
				movements: movementsColumnFiltersState,
				customers: customersColumnFiltersState,
			}[currentView],
			setColumnFilters,
			columnVisibility: columnVisibilityState,
			setColumnVisibility,
			tableRef,
			paymentHashtable: displayHashtable,
			processingProgress: totalPayments
				? (processingProgress / totalPayments) * 100
				: 0,
			customersData: mappedCustomers,
			customersLoading,
			movementsProcessingProgress,
		}),
		[
			payments,
			movements,
			filteredPayments,
			combinedPaymentsLoading,
			currentView,
			storeFilter,
			ordersColumnFiltersState,
			movementsColumnFiltersState,
			customersColumnFiltersState,
			columnVisibilityState,
			displayHashtable,
			processingProgress,
			totalPayments,
			customersData,
			customersLoading,
			movementsProcessingProgress,
		]
	)

	return (
		<TableDataContext.Provider value={value}>
			{children}
		</TableDataContext.Provider>
	)
}

export function useTableData() {
	const context = useContext(TableDataContext)
	if (context === undefined) {
		throw new Error("useTableData must be used within a TableDataProvider")
	}
	return context
}
